import { Card, CardBody, CardHeader } from '@heroui/react';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, NavLink, Outlet } from 'react-router-dom';
import { pickBilingual } from '@lims/shared-types';
import { LanguageSwitcher } from '../components/LanguageSwitcher.js';
import { ActionButton, Alert, ErrorState, LoadingState } from '../components/ui/index.js';
import { currentLocale } from '../i18n/index.js';
import { portalApi } from './portal-api.js';
import { PORTAL_QUERY_KEY, usePortalSession } from './PortalSession.js';

/**
 * The client portal's OWN shell — neither the staff `AppShell` nor the
 * public `PublicShell`. Designed at 390 px first: one column, the navigation
 * wrapping rather than scrolling sideways, logical spacing throughout so
 * Arabic mirrors without a branch.
 *
 * The laboratory's name comes from `GET /auth/branding` before sign-in and
 * from `/portal/me` after; nothing here names a laboratory of its own
 * (CLAUDE.md section 6.1).
 */

/** Sign-in and password pages: a centred card under the laboratory's name. */
export function PortalPublicLayout({
  title,
  subtitle,
  children,
}: {
  readonly title: string;
  readonly subtitle?: string;
  readonly children: ReactNode;
}) {
  const { t } = useTranslation();
  const locale = currentLocale();
  const branding = useQuery({
    queryKey: [...PORTAL_QUERY_KEY, 'branding'],
    queryFn: portalApi.branding,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const laboratoryName = branding.data?.tenant
    ? pickBilingual({ ar: branding.data.tenant.nameAr, en: branding.data.tenant.nameEn }, locale)
    : null;

  return (
    <div className="flex min-h-screen flex-col bg-default-50">
      <div className="flex justify-end px-4 pt-4 sm:px-6">
        <LanguageSwitcher />
      </div>
      <main id="main" className="flex flex-1 items-center justify-center px-4 py-8 sm:px-6">
        <div className="w-full max-w-md">
          <p className="mb-stack text-center text-body font-medium text-default-700">
            {laboratoryName ?? t('portal.title')}
          </p>
          <Card shadow="sm">
            <CardHeader className="flex flex-col items-start gap-1 px-6 pt-6">
              <h1 className="text-title font-semibold text-default-900">{title}</h1>
              {subtitle ? <p className="text-body text-default-600">{subtitle}</p> : null}
            </CardHeader>
            <CardBody className="gap-stack px-6 pb-6">{children}</CardBody>
          </Card>
          <p className="mt-stack text-center text-caption text-default-600">{t('portal.clientsOnly')}</p>
        </div>
      </main>
    </div>
  );
}

/** Everything behind sign-in. Without a session, the sign-in page. */
export function RequirePortalSession() {
  const { t } = useTranslation();
  const { me, isLoading, isUnavailable, error, signOut } = usePortalSession();

  if (isLoading) return <LoadingState label={t('common.loading')} />;
  if (isUnavailable) {
    return (
      <PortalPublicLayout title={t('portal.title')}>
        <Alert tone="warning" title={t('errors.PORTAL_NOT_AVAILABLE')} />
        <ActionButton priority="secondary" onPress={() => void signOut()}>
          {t('portal.signOut')}
        </ActionButton>
      </PortalPublicLayout>
    );
  }
  if (error && !me) {
    return (
      <PortalPublicLayout title={t('portal.title')}>
        <ErrorState error={error} />
        <ActionButton priority="secondary" onPress={() => void signOut()}>
          {t('portal.signOut')}
        </ActionButton>
      </PortalPublicLayout>
    );
  }
  if (!me) return <Navigate to="/portal/login" replace />;
  return <PortalShell />;
}

function PortalNavItem({ to, end, children }: { readonly to: string; readonly end?: boolean; readonly children: ReactNode }) {
  return (
    <NavLink
      to={to}
      end={end ?? false}
      className={({ isActive }) =>
        `rounded-md px-3 py-2 text-body ${
          isActive ? 'bg-primary-50 font-medium text-primary-800' : 'text-default-700 hover:bg-default-100'
        }`
      }
    >
      {children}
    </NavLink>
  );
}

export function PortalShell() {
  const { t } = useTranslation();
  const locale = currentLocale();
  const { me, signOut, updatePreferredLocale } = usePortalSession();
  if (!me) return null;

  return (
    <div className="flex min-h-screen flex-col bg-default-50">
      <header className="border-b border-default-200 bg-content1">
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-3 px-4 py-3 sm:px-6">
          <div className="flex flex-wrap items-start justify-between gap-inline">
            <div className="min-w-0">
              <p className="text-body font-semibold text-default-900">
                {pickBilingual(me.laboratory.name, locale) ?? t('portal.title')}
              </p>
              <p className="text-caption text-default-600">
                {t('portal.signedInAs', {
                  name: me.displayName ?? me.email,
                  client: me.client.name ?? '',
                })}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-inline">
              <LanguageSwitcher onChange={(locale) => void updatePreferredLocale(locale)} />
              <ActionButton priority="tertiary" size="sm" onPress={() => void signOut()}>
                {t('portal.signOut')}
              </ActionButton>
            </div>
          </div>
          <nav aria-label={t('portal.navigation')}>
            <ul className="flex flex-wrap gap-1">
              <li>
                <PortalNavItem to="/portal" end>
                  {t('portal.nav.requests')}
                </PortalNavItem>
              </li>
              <li>
                <PortalNavItem to="/portal/reports">{t('portal.nav.reports')}</PortalNavItem>
              </li>
              {/* Hidden without financial visibility; the API refuses those routes anyway. */}
              {me.financialVisibility ? (
                <>
                  <li>
                    <PortalNavItem to="/portal/invoices">{t('portal.nav.invoices')}</PortalNavItem>
                  </li>
                  <li>
                    <PortalNavItem to="/portal/statement">{t('portal.nav.statement')}</PortalNavItem>
                  </li>
                </>
              ) : null}
            </ul>
          </nav>
        </div>
      </header>
      <main id="main" className="mx-auto w-full max-w-5xl flex-1 px-4 py-6 sm:px-6">
        <Outlet />
      </main>
    </div>
  );
}
